import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { api, ApiError } from '@/api/client';

export type CurrentUser = {
  email: string;
  name: string;
};

/** A CRM customer (asiakkuus) the signed-in contact may act for. */
export type Customer = {
  id: string;
  name: string;
  type: string;
  businessId: string | null;
  roles: string[];
};

type SessionView = {
  user: CurrentUser;
  customers: Customer[];
  activeCustomer: Customer | null;
};

export const useAuthStore = defineStore('auth', () => {
  const user = ref<CurrentUser | null>(null);
  const customers = ref<Customer[]>([]);
  const activeCustomer = ref<Customer | null>(null);
  const initialized = ref(false);
  const lastError = ref<string | null>(null);
  // null until /auth/config resolves; true → Logto flow, false → dev password form.
  const logtoEnabled = ref<boolean | null>(null);

  const hasSeveralCustomers = computed(() => customers.value.length > 1);

  function apply(view: SessionView | null): void {
    user.value = view?.user ?? null;
    customers.value = view?.customers ?? [];
    activeCustomer.value = view?.activeCustomer ?? null;
  }

  /**
   * Only a 401 means "not signed in". A 429 (the API throttles per IP), a
   * 5xx or a network error is transient: retry once after a short pause and,
   * if it still fails, keep whatever session we had and flag the outage so
   * the login page can say so instead of pretending the user signed out.
   */
  async function bootstrap(): Promise<void> {
    if (initialized.value) return;
    try {
      apply(await api<SessionView>('/auth/me'));
    } catch (err) {
      if (isSignedOut(err)) {
        apply(null);
      } else {
        await new Promise((r) => setTimeout(r, 1500));
        try {
          apply(await api<SessionView>('/auth/me'));
        } catch (again) {
          if (isSignedOut(again)) {
            apply(null);
          } else {
            lastError.value = 'service_unavailable';
            // eslint-disable-next-line no-console
            console.warn('auth bootstrap failed (transient)', again);
          }
        }
      }
    } finally {
      initialized.value = true;
    }
  }

  async function loadConfig(): Promise<void> {
    if (logtoEnabled.value !== null) return;
    try {
      const cfg = await api<{ logto: boolean }>('/auth/config');
      logtoEnabled.value = cfg.logto;
    } catch {
      // If config can't be read, fall back to showing the dev password form.
      logtoEnabled.value = false;
    }
  }

  // Logto (BFF) — full-page redirects handled by the backend.
  function loginWithLogto(next?: string): void {
    window.location.href = withNext('/auth/login', next);
  }
  function registerWithLogto(next?: string): void {
    window.location.href = withNext('/auth/register', next);
  }

  // Dev/test password fallback (only reachable when logtoEnabled === false).
  async function login(email: string, password: string): Promise<void> {
    lastError.value = null;
    try {
      apply(await api<SessionView>('/auth/login', { method: 'POST', body: { email, password } }));
    } catch (err) {
      if (err instanceof ApiError) {
        lastError.value = err.status === 429 ? 'too_many_attempts' : err.code;
      } else {
        lastError.value = 'network_error';
      }
      throw err;
    }
  }

  /** The server accepts only a customer from a fresh CRM lookup. */
  async function switchCustomer(customerId: string): Promise<void> {
    apply(await api<SessionView>('/auth/switch-customer', { method: 'POST', body: { customerId } }));
  }

  function logout(): void {
    // GET /auth/logout clears the session cookie and, with Logto on, ends the
    // Logto session before returning to /login. Works in both modes.
    window.location.href = '/auth/logout';
  }

  return {
    user,
    customers,
    activeCustomer,
    hasSeveralCustomers,
    initialized,
    lastError,
    logtoEnabled,
    bootstrap,
    loadConfig,
    loginWithLogto,
    registerWithLogto,
    login,
    switchCustomer,
    logout,
  };
});

function isSignedOut(err: unknown): boolean {
  return err instanceof ApiError && err.status === 401;
}

function withNext(path: string, next?: string): string {
  if (!next) return path;
  return `${path}?next=${encodeURIComponent(next)}`;
}
